import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { listarEquipo, invitar, cancelarInvitacion, quitarMiembro, linkInvitacion } from '../services/equipoService';
import { copiarTexto } from '../utils/compartir';
import PermisosMiembro from './PermisosMiembro';
import { EN_TIENDA, segunTienda } from '../utils/tienda';

const fecha = (f) => (f ? new Date(f).toLocaleDateString('es-HN', { day: 'numeric', month: 'short' }) : '');
const ROLES = { duena: 'Dueña', vendedora: 'Vendedor/a' };

// "Mi equipo" (solo la dueña): invitar con enlace por WhatsApp, ver puestos
// usados del plan, cancelar invitaciones y quitar miembros.
export default function EquipoView({ onVolver, onVerPlanes }) {
  const { negocio } = useAuth();
  const { mostrarToast } = useToast();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [nueva, setNueva] = useState(null); // invitación recién creada (para compartir)
  const [permisosDe, setPermisosDe] = useState(null); // user_id con el panel de permisos abierto

  const cargar = useCallback(async () => {
    try { setDatos(await listarEquipo()); setError(null); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const mensaje = (inv) =>
    `¡Hola! Te invito a unirte al equipo de ${negocio?.nombre || 'mi negocio'} en Kontrol 💜\n` +
    `Crea tu contraseña aquí (usa el correo ${inv.email}):\n${linkInvitacion(inv.token)}\n` +
    'El enlace vence en 7 días.';

  const enviarWhatsApp = (inv) =>
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(mensaje(inv))}`, '_blank', 'noopener,noreferrer');

  const copiar = async (inv) => {
    if (await copiarTexto(linkInvitacion(inv.token))) mostrarToast('Enlace copiado ✅', 'exito');
  };

  const crear = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEnviando(true);
    try {
      const inv = await invitar(email);
      setNueva(inv);
      setEmail('');
      await cargar();
    } catch (err) {
      mostrarToast(err.message, 'error', 7000);
    }
    setEnviando(false);
  };

  const cancelar = async (inv) => {
    if (!window.confirm(`¿Cancelar la invitación de ${inv.email}? El enlace dejará de funcionar.`)) return;
    try { await cancelarInvitacion(inv.id); if (nueva?.id === inv.id) setNueva(null); await cargar(); mostrarToast('Invitación cancelada', 'exito'); }
    catch (err) { mostrarToast(err.message, 'error'); }
  };

  const quitar = async (m) => {
    if (!window.confirm(`¿Quitar a ${m.email} de tu negocio? Ya no podrá entrar a Kontrol de ${negocio?.nombre || 'tu negocio'}. Las ventas que registró se quedan.`)) return;
    try { await quitarMiembro(m.user_id); await cargar(); mostrarToast('Usuario quitado del equipo', 'exito'); }
    catch (err) { mostrarToast(err.message, 'error'); }
  };

  const lleno = datos && datos.usados >= datos.limite;
  const fila = { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0', borderTop: '1px solid #F3F0FA', flexWrap: 'wrap' };
  const btnSoft = { border: '1px solid #E5E7EB', background: '#fff', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px' };

  return (
    <div className="k-page">
      <div className="k-page-head">
        <h2 className="k-page-title">👥 Mi equipo</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>

      {error && <div className="k-card" style={{ padding: '16px', color: '#B91C1C' }}>⚠️ {error}</div>}
      {!datos && !error && <p style={{ color: '#6B7280' }}>Cargando…</p>}

      {datos && (
        <>
          <div className="k-card" style={{ padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <b style={{ fontSize: '15px' }}>Usuarios: {datos.usados} de {datos.limite}</b>
              {lleno && !EN_TIENDA && <button onClick={onVerPlanes} className="k-btn-soft">💎 Subir de plan</button>}
            </div>
            <div style={{ height: '8px', background: '#F3F0FA', borderRadius: '999px', marginTop: '10px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, (datos.usados / Math.max(1, datos.limite)) * 100)}%`, height: '100%', background: lleno ? '#B45309' : 'var(--color-primario,#6D28D9)' }} />
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#6B7280' }}>
              Por defecto, tu equipo puede vender y ver el inventario, clientes, pedidos, servicios y agenda, sin ver tus costos, ganancias, gastos, cuentas ni reportes. Con 🔐 Permisos le das más accesos a cada persona (por ejemplo, el corte del día si sales de viaje).
            </p>
          </div>

          <form onSubmit={crear} className="k-card" style={{ padding: '16px', marginBottom: '16px' }}>
            <b style={{ display: 'block', marginBottom: '8px' }}>Invitar a alguien</b>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={enviando || lleno}
                placeholder="Correo de tu vendedor/a" autoComplete="off"
                style={{ flex: 1, minWidth: '200px', padding: '11px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit' }} />
              <button type="submit" disabled={enviando || lleno || !email.trim()}
                style={{ padding: '11px 16px', border: 'none', borderRadius: '10px', background: lleno ? '#9CA3AF' : 'var(--color-primario,#6D28D9)', color: '#fff', fontWeight: 700, cursor: lleno ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                {enviando ? 'Creando…' : 'Crear invitación'}
              </button>
            </div>
            {lleno && <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#B45309' }}>{segunTienda('Llegaste al límite de tu plan. Sube de plan para agregar más personas.', 'Llegaste al límite de usuarios de tu plan.')}</p>}

            {nueva && (
              <div style={{ marginTop: '14px', background: '#F5F3FF', borderRadius: '10px', padding: '12px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '14px' }}>✅ Invitación lista para <b>{nueva.email}</b>. Mándale el enlace:</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => enviarWhatsApp(nueva)} style={{ ...btnSoft, background: '#25D366', color: '#fff', border: 'none', fontWeight: 700 }}>Enviar por WhatsApp</button>
                  <button type="button" onClick={() => copiar(nueva)} style={btnSoft}>Copiar enlace</button>
                </div>
              </div>
            )}
          </form>

          <div className="k-card" style={{ padding: '4px 16px 8px' }}>
            <b style={{ display: 'block', padding: '12px 0 4px' }}>Personas en tu negocio</b>
            {datos.miembros.map((m) => (
              <div key={m.user_id} style={fila}>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{m.email}{m.soy_yo ? ' (tú)' : ''}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>{ROLES[m.rol] || m.rol} · desde {fecha(m.desde)}</div>
                </div>
                {!m.soy_yo && m.rol !== 'duena' && (
                  <button onClick={() => setPermisosDe(permisosDe === m.user_id ? null : m.user_id)} style={btnSoft}>
                    🔐 Permisos{Object.values(m.permisos || {}).filter(Boolean).length ? ` (${Object.values(m.permisos).filter(Boolean).length})` : ''}
                  </button>
                )}
                {!m.soy_yo && m.rol !== 'duena' && <button onClick={() => quitar(m)} style={{ ...btnSoft, color: '#B91C1C' }}>Quitar</button>}
                {permisosDe === m.user_id && (
                  <PermisosMiembro miembro={m} onCerrar={() => setPermisosDe(null)}
                    onGuardado={(nuevos) => {
                      setDatos((d) => ({ ...d, miembros: d.miembros.map((x) => (x.user_id === m.user_id ? { ...x, permisos: nuevos } : x)) }));
                      setPermisosDe(null);
                    }} />
                )}
              </div>
            ))}

            {datos.invitaciones.length > 0 && <b style={{ display: 'block', padding: '14px 0 4px' }}>Invitaciones pendientes</b>}
            {datos.invitaciones.map((inv) => (
              <div key={inv.id} style={fila}>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{inv.email}</div>
                  <div style={{ fontSize: '12px', color: '#B45309' }}>⏳ Pendiente · vence el {fecha(inv.expira_en)}</div>
                </div>
                <button onClick={() => enviarWhatsApp(inv)} style={btnSoft}>WhatsApp</button>
                <button onClick={() => copiar(inv)} style={btnSoft}>Copiar</button>
                <button onClick={() => cancelar(inv)} style={{ ...btnSoft, color: '#B91C1C' }}>Cancelar</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
